import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrModule } from 'ngx-toastr';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import localeTr from '@angular/common/locales/tr';
import { registerLocaleData } from '@angular/common';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './views/login/login.component';
import { CoachLayoutComponent } from './views/layout/coach-layout/coach-layout.component';
import { CoachSidebarComponent } from './views/layout/coach-layout/coach-sidebar/coach-sidebar.component';
import { CoachHeaderComponent } from './views/layout/coach-layout/coach-header/coach-header.component';
import { PlaceholderComponent } from './views/placeholder/placeholder.component';
import { MetricsComponent } from './views/metrics/metrics.component';
import { AccountComponent } from './views/account/account.component';
import { TicketsComponent } from './views/tickets/tickets.component';
import { StudentsComponent } from './views/students/students.component';
import { StudentDetailComponent } from './views/students/student-detail/student-detail.component';
import { StudentAvatarComponent } from './shared/student-avatar/student-avatar.component';
import { PlanMeetingModalComponent } from './views/students/plan-meeting-modal/plan-meeting-modal.component';
import { NaviRouteCardComponent } from './views/students/navi-route-card/navi-route-card.component';
import { ExamHistoryCardComponent } from './views/students/exam-history-card/exam-history-card.component';
import { StudentTabsComponent } from './views/students/student-tabs/student-tabs.component';
import { PlannerTabComponent } from './views/students/planner-tab/planner-tab.component';
import {
  PlannerItemModalComponent,
} from './views/students/planner-tab/planner-item-modal/planner-item-modal.component';
import {
  PlannerTransferModalComponent,
} from './views/students/planner-tab/planner-transfer-modal/planner-transfer-modal.component';
import { PlannerPlanModalComponent } from './views/students/planner-tab/planner-plan-modal/planner-plan-modal.component';
import {
  PlannerDeletePlanModalComponent,
} from './views/students/planner-tab/planner-delete-plan-modal/planner-delete-plan-modal.component';
import {
  MeetingNotesTabComponent,
} from './views/students/meeting-notes-tab/meeting-notes-tab.component';
import {
  AppointmentNotesPanelComponent,
} from './views/students/meeting-notes-tab/appointment-notes-panel/appointment-notes-panel.component';
import {
  StudentNotesPanelComponent,
} from './views/students/meeting-notes-tab/student-notes-panel/student-notes-panel.component';
import { TasksTabComponent } from './views/students/tasks-tab/tasks-tab.component';
import { MessagesTabComponent } from './views/students/messages-tab/messages-tab.component';
import { MessagesComponent } from './views/messages/messages.component';
import { VideosTabComponent } from './views/students/videos-tab/videos-tab.component';
import { ContentCardComponent } from './views/students/videos-tab/content-card/content-card.component';
import {
  ContentPreviewModalComponent
} from './views/students/videos-tab/content-preview-modal/content-preview-modal.component';
import {
  AssignContentModalComponent
} from './views/students/videos-tab/assign-content-modal/assign-content-modal.component';
import {
  WeeklyReportTabComponent,
} from './views/students/weekly-report-tab/weekly-report-tab.component';
import { ChatModule } from '@gray/ui/chat/chat.module';
import { ContentModule } from '@gray/ui/content/content.module';
import { SocketIoModule } from 'ngx-socket-io';
import { PlannerModule } from '@gray/ui/planner/planner.module';
import { AgendaComponent } from './views/agenda/agenda.component';
import { AgendaWeekComponent } from './views/agenda/agenda-week/agenda-week.component';
import { AgendaTodayComponent } from './views/agenda/agenda-today/agenda-today.component';
import { AgendaMonthComponent } from './views/agenda/agenda-month/agenda-month.component';
import { AppointmentFormModalComponent } from './views/agenda/appointment-form-modal/appointment-form-modal.component';
import { CancelAppointmentModalComponent } from './views/agenda/cancel-appointment-modal/cancel-appointment-modal.component';
import { RejectRescheduleModalComponent } from './views/agenda/reject-reschedule-modal/reject-reschedule-modal.component';
import {
  RescheduleRequestRowComponent
} from './views/agenda/reschedule-request-row/reschedule-request-row.component';
import {
  RescheduleRequestsModalComponent
} from './views/agenda/reschedule-requests-modal/reschedule-requests-modal.component';
import { AvailabilityModalComponent } from './views/agenda/availability-modal/availability-modal.component';
import { UiModule } from '@gray/ui/ui.module';
import { GRAY_UI_CONFIG, IGrayUiConfig } from '@gray/ui/ui.config';
import { GRAY_API_URL, GRAY_AUTH_COOKIE_NAME } from '@gray/domain/http/http.config';
import { ApiService } from '@gray/domain/http/api.service';
import { JwtService } from '@gray/domain/http/jwt.service';
import { translateLoaderFactory } from './i18n/translate-loader';
import { environment } from '../environments/environment';
import { CoachJwtInterceptor } from './core/interceptors/coach-jwt.interceptor';
import { CoachErrorInterceptor } from './core/interceptors/coach-error.interceptor';
import { CoachAuthService } from './core/services/coach-auth.service';

registerLocaleData(localeTr);

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    CoachLayoutComponent,
    CoachSidebarComponent,
    CoachHeaderComponent,
    PlaceholderComponent,
    AccountComponent,
    MetricsComponent,
    TicketsComponent,
    StudentsComponent,
    StudentDetailComponent,
    StudentAvatarComponent,
    PlanMeetingModalComponent,
    NaviRouteCardComponent,
    ExamHistoryCardComponent,
    StudentTabsComponent,
    PlannerTabComponent,
    MeetingNotesTabComponent,
    AppointmentNotesPanelComponent,
    StudentNotesPanelComponent,
    TasksTabComponent,
    MessagesTabComponent,
    MessagesComponent,
    VideosTabComponent,
    ContentCardComponent,
    ContentPreviewModalComponent,
    AssignContentModalComponent,
    WeeklyReportTabComponent,
    PlannerItemModalComponent,
    PlannerTransferModalComponent,
    PlannerPlanModalComponent,
    PlannerDeletePlanModalComponent,
    AgendaComponent,
    AgendaWeekComponent,
    AgendaTodayComponent,
    AgendaMonthComponent,
    AppointmentFormModalComponent,
    CancelAppointmentModalComponent,
    RejectRescheduleModalComponent,
    AvailabilityModalComponent,
    RescheduleRequestRowComponent,
    RescheduleRequestsModalComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    NgbModule,
    FormsModule,
    ReactiveFormsModule,
    UiModule,
    PlannerModule,
    ChatModule,
    ContentModule,
    SocketIoModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateLoaderFactory,
        deps: [HttpClient],
      },
    }),
    SweetAlert2Module.forRoot(),
    NgxSkeletonLoaderModule.forRoot(),
    ToastrModule.forRoot({
      progressBar: true,
      timeOut: 4000,
      easeTime: 200,
      maxOpened: 1,
      preventDuplicates: true,
      positionClass: 'toast-bottom-center',
    }),
  ],
  providers: [
    {
      provide: LOCALE_ID,
      useValue: 'tr-TR',
    },
    {
      provide: GRAY_API_URL,
      useValue: environment.apiUrl,
    },
    {
      provide: GRAY_AUTH_COOKIE_NAME,
      useValue: environment.authCookieName,
    },
    {
      provide: GRAY_UI_CONFIG,
      useValue: {
        defaultProfilePhoto: `${environment.cdnBase}/assets/img/default-profile-photo.jpg`,
        flowPlayerToken: environment.flowPlayerToken,
        videoProxyToken: environment.videoProxyToken,
      } as IGrayUiConfig,
    },
    ApiService,
    JwtService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CoachJwtInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CoachErrorInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (auth: CoachAuthService) => {
        return () => auth.loadCoach();
      },
      deps: [CoachAuthService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}
